/**
 * phaseCasualtyTable.js v1.0.0 - 2026-10-13 (Tab 2 plan, step 2: ONE casualty table)
 *
 * One table for the daily battle casualty rate of a phase, replacing the three overlapping multipliers that used to be
 * stacked (operation type x posture x mission role, plus the sub-phase role x operation type multiplier).
 *
 *   rate factor = form of battle (at the enemy resistance of the phase)
 *               x maneuver group (attack forms only)
 *               x special condition (by day of the phase)
 *
 * Scale: relative to an attack against HEAVY resistance = 1.00, which is the model reference of 1.5 percent of strength
 * per day (BASE_CASUALTY_RATE.daily). Korean War regiments: attack against heavy resistance 18.41 killed + wounded per
 * 1,000 per day = 1.00 on this scale.
 *
 * Resistance: the enemy combat power index of the phase (1.0 = moderate; anchors 0.5 light, 1.0 moderate, 2.0 heavy). The
 * factor between the anchors is interpolated on a log scale, so there are no steps. Beyond the anchors it is capped.
 *
 * Status of every number: SOURCED, DERIVED or EDUCATED_GUESS (see docs/planning/TAB2_PROPOSAL_TABLE.md, docs/research/RESEARCH_PHASE_RATES.md).
 * Nothing is deleted: the old multipliers stay in operationsConfig.js / postures.js and are used when
 * USE_PHASE_CASUALTY_TABLE is false.
 */

import { TRAINING_LEVELS, EQUIPMENT_LEVELS, MORALE_LEVELS } from './combatEffectivenessModifiers';

export const USE_PHASE_CASUALTY_TABLE = true;

// 2026-10-13 (Tab 2 plan, step 3): the enemy assessment sets the resistance, so the x0.50 to x1.70 force superiority
// multiplier (FORCE_SUPERIORITY) is not applied to the casualties as well: each factor counts once. The code stays; set to true to
// restore the old multiplier (then also stop deriving the resistance from the enemy assessment).
export const FORCE_SUPERIORITY_MULTIPLIER_IN_CHAIN = false;

export const RESISTANCE_ANCHORS = Object.freeze({
  light: 0.5,
  moderate: 1.0,
  heavy: 2.0,
  status: 'EDUCATED_GUESS',
  basis: 'Anchors of the enemy combat power index (strength ratio x training x equipment x morale). Heavy at 2.0 kept by Dr. Tatar 2026-10-13; a value of 3 to be reconsidered if Monte Carlo worst cases need it.'
});

export const RESISTANCE_LEVEL_INDEX = Object.freeze({
  LIGHT: RESISTANCE_ANCHORS.light,
  MODERATE: RESISTANCE_ANCHORS.moderate,
  HEAVY: RESISTANCE_ANCHORS.heavy
});

// ---------------------------------------------------------------------------------------------------------------
// Layer A: form of battle. light / moderate / heavy = factor at that resistance.
// family 'ATTACK' accepts a maneuver group; 'MARCH' forms are used for sub-phase marches only (a march phase runs through
// the separate march rate path of the calculator).
// ---------------------------------------------------------------------------------------------------------------
export const PHASE_FORMS = Object.freeze({
  HASTY_ATTACK:       { family: 'ATTACK',    light: 0.19,  moderate: 0.44, heavy: 1.00, status: 'SOURCED',
    basis: 'Korean War regiments: attack against heavy resistance 18.41, light resistance 3.45 per 1,000 per day (Table 23).' },
  DELIBERATE_ATTACK:  { family: 'ATTACK',    light: 0.14,  moderate: 0.33, heavy: 0.75, status: 'EDUCATED_GUESS',
    basis: 'Ratio 0.9 / 1.2 of the former posture table to the hasty attack.' },
  MOVEMENT_TO_CONTACT:{ family: 'ATTACK',    light: 0.10,  moderate: 0.25, heavy: 0.60, status: 'EDUCATED_GUESS', basis: 'No data.' },
  EXPLOITATION:       { family: 'ATTACK',    light: 0.05,  moderate: 0.15, heavy: 0.47, status: 'DERIVED',
    basis: 'Pursuit-phase regiments: heavy resistance 8.57, light 0.97 per 1,000 per day (Table 24); the winner loses less.' },
  PURSUIT:            { family: 'ATTACK',    light: 0.05,  moderate: 0.15, heavy: 0.47, status: 'SOURCED', basis: 'Korean War Table 24 (pursuit and mopping-up phases).' },
  CONSOLIDATION:      { family: 'DEFENSE',   light: 0.03,  moderate: 0.08, heavy: 0.20, status: 'DERIVED',
    basis: 'Mopping-up or consolidating gains 3.34 per 1,000 per day (Table 23), 0.18 of the heavy attack; split by resistance by guess.' },
  DEFENSE_AREA:       { family: 'DEFENSE',   light: 0.095, moderate: 0.23, heavy: 0.57, status: 'SOURCED',
    basis: 'Korean War regiments: defense against enemy attack 10.40, holding positions 1.75 per 1,000 per day (Table 23).' },
  DEFENSE_HASTY:      { family: 'DEFENSE',   light: 0.13,  moderate: 0.33, heavy: 0.80, status: 'EDUCATED_GUESS', basis: 'Area defense x 1.4 (ratio 0.85 / 0.6 of the former posture table).' },
  DEFENSE_MOBILE:     { family: 'DEFENSE',   light: 0.10,  moderate: 0.26, heavy: 0.65, status: 'EDUCATED_GUESS', basis: 'No data.' },
  DELAY:              { family: 'RETROGRADE',light: 0.03,  moderate: 0.12, heavy: 0.45, status: 'EDUCATED_GUESS', basis: 'Anchored on withdrawal and rearguard 0.56 per 1,000 per day (Table 24).' },
  WITHDRAWAL:         { family: 'RETROGRADE',light: 0.02,  moderate: 0.06, heavy: 0.20, status: 'EDUCATED_GUESS', basis: 'Anchored on withdrawal and rearguard 0.56 per 1,000 per day (Table 24).' },
  RETIREMENT:         { family: 'RETROGRADE',light: 0.01,  moderate: 0.01, heavy: 0.01, status: 'EDUCATED_GUESS', basis: 'Out of contact: movement only.' },
  RESERVE:            { family: 'RESERVE',   light: 0.002, moderate: 0.002,heavy: 0.002,status: 'SOURCED', basis: 'Division reserve 0.02 vs offensive 1.93 wounded per 1,000 per day (Table 21).' },
  SECURITY:           { family: 'SECURITY',  light: 0.05,  moderate: 0.05, heavy: 0.05, status: 'SOURCED', basis: 'Patrolling 0.96 per 1,000 per day (Table 23).' },
  STABILITY:          { family: 'SECURITY',  light: 0.03,  moderate: 0.08, heavy: 0.20, status: 'EDUCATED_GUESS', basis: 'Former operation type STABILITY_OPERATIONS 0.3 / posture 0.2; no data.' },
  TACTICAL_MARCH:     { family: 'MARCH',     light: 0.067, moderate: 0.067,heavy: 0.067,status: 'DERIVED', basis: 'March battle rate constant 0.1 percent a day / 1.5 percent.' },
  ADMINISTRATIVE_MARCH:{family: 'MARCH',     light: 0.007, moderate: 0.007,heavy: 0.007,status: 'DERIVED', basis: 'March battle rate constant 0.01 percent a day / 1.5 percent.' }
});

// ---------------------------------------------------------------------------------------------------------------
// Layer B: maneuver group of an attack (merged forms with the same factor).
// ---------------------------------------------------------------------------------------------------------------
export const MANEUVER_GROUPS = Object.freeze({
  FRONTAL:           { factor: 1.00, label: 'Frontal attack / penetration', status: 'EDUCATED_GUESS' },
  FLANK:             { factor: 0.75, label: 'Flank / indirect (envelopment, turning movement, infiltration)', status: 'EDUCATED_GUESS' },
  LIMITED_OBJECTIVE: { factor: 0.60, label: 'Limited-objective attack (raid, spoiling attack)', status: 'EDUCATED_GUESS' },
  FEINT_FIXING:      { factor: 0.40, label: 'Feint / demonstration / fixing', status: 'EDUCATED_GUESS' }
});

// Sub-phase / phase role -> group. MAIN_EFFORT and SUPPORTING are both frontal (the former x1.5 / x1.0 are replaced).
export const ROLE_TO_GROUP = Object.freeze({
  MAIN_EFFORT: 'FRONTAL',
  SUPPORTING: 'FRONTAL',
  EXPLOITATION: 'FLANK',
  ECONOMY_OF_FORCE: 'LIMITED_OBJECTIVE',
  FEINT: 'FEINT_FIXING',
  FIXING: 'FEINT_FIXING',
  RESERVE: null   // a reserve role makes the sub-phase a reserve (form RESERVE), not a group
});

// ---------------------------------------------------------------------------------------------------------------
// Layer C: special condition. Multiplier on the same form at the same resistance, by day of the phase.
// ---------------------------------------------------------------------------------------------------------------
export const DEFAULT_FOOTHOLD_DAY = Object.freeze({ RIVER_ASSAULT: 2, AIRBORNE: 3 });   // first day without the assault factor; EDUCATED_GUESS

export const SPECIAL_CONDITIONS = Object.freeze({
  NONE:              { label: 'None', status: 'SOURCED',
    factorForDay: () => 1.0 },
  URBAN:             { label: 'Urban', status: 'SOURCED',
    basis: 'Hue 1968 about 1.6 percent a day (USNI), Dupuy Institute: attacker casualty rates not higher in urban fights; the advance is slower (one-half to one-third).',
    factorForDay: () => 1.0 },
  RIVER_ASSAULT:     { label: 'River crossing (assault, then bridgehead)', status: 'DERIVED',
    basis: 'Korean War assault river crossing 9.87 vs weighted attack 7.74 per 1,000 per day = 1.3 (39 regiment days); bridgehead 1.0 by guess.',
    factorForDay: (day, p) => (day < (p.foothold_day ?? DEFAULT_FOOTHOLD_DAY.RIVER_ASSAULT) ? 1.3 : 1.0) },
  AMPHIBIOUS:        { label: 'Amphibious assault (by landing day)', status: 'DERIVED',
    basis: 'Landing day 4.0 (Omaha 4.7, Tarawa 4, Iwo Jima 5.4 times the 1.5 percent reference); decay 2.0, 1.3, 1.0 by guess.',
    factorForDay: (day) => (day <= 1 ? 4.0 : day === 2 ? 2.0 : day === 3 ? 1.3 : 1.0) },
  AIRBORNE:          { label: 'Airborne / air assault (until link-up)', status: 'EDUCATED_GUESS',
    basis: 'Former operation type value 1.5 (unsourced), applied until the link-up day.',
    factorForDay: (day, p) => (day < (p.foothold_day ?? DEFAULT_FOOTHOLD_DAY.AIRBORNE) ? 1.5 : 1.0) },
  BREACH:            { label: 'Breach of an obstacle', status: 'EDUCATED_GUESS', basis: 'No data.', factorForDay: () => 1.3 },
  FORTIFIED_ASSAULT: { label: 'Assault on fortified / trench positions', status: 'EDUCATED_GUESS', basis: 'No data.', factorForDay: () => 1.3 },
  RELIEF_IN_PLACE:   { label: 'Relief in place / passage of lines', status: 'EDUCATED_GUESS', basis: 'No data.', factorForDay: () => 0.8 },
  STABILITY:         { label: 'Stability operations', status: 'EDUCATED_GUESS', basis: 'Former operation type value 0.3 (unsourced).', factorForDay: () => 0.3 }
});

// Former scenario-level operation type (operationsConfig OPERATION_TYPES) -> special condition. Mountain stays terrain.
export const OPERATION_TYPE_TO_CONDITION = Object.freeze({
  GROUND_COMBAT: 'NONE',
  URBAN_COMBAT: 'URBAN',
  RIVER_CROSSING: 'RIVER_ASSAULT',
  MOUNTAIN_OPERATIONS: 'NONE',
  AMPHIBIOUS_ASSAULT: 'AMPHIBIOUS',
  AIRBORNE_ASSAULT: 'AIRBORNE',
  STABILITY_OPERATIONS: 'STABILITY',
  MARCH: 'NONE'
});

// Phase posture (editor menu, postures.js, operationsConfig) -> form, and a condition / group the posture itself carries.
export const POSTURE_TO_FORM = Object.freeze({
  ATTACK: { form: 'HASTY_ATTACK' },
  HASTY_ATTACK: { form: 'HASTY_ATTACK' },
  DELIBERATE_ATTACK: { form: 'DELIBERATE_ATTACK' },
  MOVEMENT_TO_CONTACT: { form: 'MOVEMENT_TO_CONTACT' },
  EXPLOITATION: { form: 'EXPLOITATION' },
  PURSUIT: { form: 'PURSUIT' },
  DEFENSE: { form: 'DEFENSE_AREA' },
  DELIBERATE_DEFENSE: { form: 'DEFENSE_AREA' },
  AREA_DEFENSE: { form: 'DEFENSE_AREA' },
  HASTY_DEFENSE: { form: 'DEFENSE_HASTY' },
  MOBILE_DEFENSE: { form: 'DEFENSE_MOBILE' },
  RETROGRADE: { form: 'DELAY' },
  DELAY: { form: 'DELAY' },
  WITHDRAWAL: { form: 'WITHDRAWAL' },
  RETIREMENT: { form: 'RETIREMENT' },
  CONSOLIDATION: { form: 'CONSOLIDATION' },
  RESERVE: { form: 'RESERVE' },
  RECONSTITUTION: { form: 'RESERVE' },
  STABILITY_OPERATIONS: { form: 'STABILITY' },
  SUPPORT_OPERATIONS: { form: 'SECURITY' },
  SECURITY: { form: 'SECURITY' },
  URBAN_ASSAULT: { form: 'HASTY_ATTACK', condition: 'URBAN' },
  RIVER_CROSSING: { form: 'DELIBERATE_ATTACK', condition: 'RIVER_ASSAULT' },
  AIRBORNE_OPERATION: { form: 'HASTY_ATTACK', condition: 'AIRBORNE' },
  BREACH: { form: 'DELIBERATE_ATTACK', condition: 'BREACH' },
  INFILTRATION: { form: 'DELIBERATE_ATTACK', group: 'FLANK' }
});

// Postures that run through the separate march rate path of the calculator (no form factor here).
export const MARCH_POSTURES = Object.freeze(['MARCH', 'MARCH_TACTICAL', 'MARCH_ADMINISTRATIVE', 'TRANSIT']);

// Sub-phase operation type (BrigadePhaseTab menu) -> form / condition.
export const SUBPHASE_OPERATION_TO_FORM = Object.freeze({
  DELIBERATE_ATTACK: { form: 'DELIBERATE_ATTACK' },
  HASTY_ATTACK: { form: 'HASTY_ATTACK' },
  MOVEMENT_TO_CONTACT: { form: 'MOVEMENT_TO_CONTACT' },
  MOBILE_DEFENSE: { form: 'DEFENSE_MOBILE' },
  RETIREMENT: { form: 'RETIREMENT' },
  URBAN: { form: 'HASTY_ATTACK', condition: 'URBAN' },
  RIVER_CROSSING: { form: 'DELIBERATE_ATTACK', condition: 'RIVER_ASSAULT' },
  EXPLOITATION: { form: 'EXPLOITATION' },
  PURSUIT: { form: 'PURSUIT' },
  AIRBORNE_ASSAULT: { form: 'HASTY_ATTACK', condition: 'AIRBORNE' },
  DEFEND: { form: 'DEFENSE_AREA' },
  HASTY_DEFENSE: { form: 'DEFENSE_HASTY' },
  DELAY: { form: 'DELAY' },
  WITHDRAWAL: { form: 'WITHDRAWAL' },
  TACTICAL_MARCH: { form: 'TACTICAL_MARCH' },
  ADMINISTRATIVE_MARCH: { form: 'ADMINISTRATIVE_MARCH' }
});

/**
 * Factor of a form at an enemy power index: log interpolation between the light, moderate and heavy values.
 * Index below the light anchor or above the heavy anchor is capped at the anchor.
 */
export function resistanceFactor(formKey, index) {
  const form = PHASE_FORMS[formKey];
  if (!form) throw new Error(`phaseCasualtyTable.resistanceFactor: unknown form '${formKey}' (valid: ${Object.keys(PHASE_FORMS).join(', ')})`);
  if (typeof index !== 'number' || !Number.isFinite(index) || index <= 0) {
    throw new Error(`phaseCasualtyTable.resistanceFactor: the enemy power index must be a positive number (got ${index})`);
  }
  const { light, moderate, heavy } = RESISTANCE_ANCHORS;
  const p = Math.min(Math.max(index, light), heavy);
  if (p <= moderate) {
    return form.light * Math.pow(form.moderate / form.light, Math.log(p / light) / Math.log(moderate / light));
  }
  return form.moderate * Math.pow(form.heavy / form.moderate, Math.log(p / moderate) / Math.log(heavy / moderate));
}

/** Resistance given as LIGHT / MODERATE / HEAVY or as a number. */
export function resistanceIndexOf(resistance) {
  if (typeof resistance === 'number') return resistance;
  const index = RESISTANCE_LEVEL_INDEX[resistance];
  if (index === undefined) {
    throw new Error(`phaseCasualtyTable.resistanceIndexOf: unknown resistance '${resistance}' (valid: ${Object.keys(RESISTANCE_LEVEL_INDEX).join(', ')} or a number)`);
  }
  return index;
}

export function levelOfIndex(index) {
  if (index < 0.75) return 'LIGHT';
  if (index <= 1.5) return 'MODERATE';
  return 'HEAVY';
}

/**
 * Resolve the casualty profile of one day of a phase. Returns null for march postures (separate march path).
 *
 * @param {Object} c
 * @param {string} c.posture            phase posture (editor menu key)
 * @param {Object} [c.phase]            phase object: maneuver_group, special_condition, foothold_day, brigade_role
 * @param {Object} [c.subPhase]         active sub-phase: role, operation_type
 * @param {string} [c.scenarioOperationType] scenario-level operation type, used as the condition when nothing else is set
 * @param {number} c.dayInPhase         1-based
 * @param {number} c.resistanceIndex    enemy power index of the phase
 */
export function resolvePhaseProfile(c) {
  const { posture, phase = {}, subPhase = null, scenarioOperationType, dayInPhase, resistanceIndex } = c;
  if (MARCH_POSTURES.includes(posture)) return null;

  let formKey;
  let conditionKey = null;
  let groupKey = null;
  let source = 'posture';

  if (subPhase && subPhase.operation_type) {
    const sp = SUBPHASE_OPERATION_TO_FORM[subPhase.operation_type];
    if (!sp) throw new Error(`phaseCasualtyTable.resolvePhaseProfile: unknown sub-phase operation type '${subPhase.operation_type}' (valid: ${Object.keys(SUBPHASE_OPERATION_TO_FORM).join(', ')})`);
    formKey = sp.form;
    conditionKey = sp.condition || null;
    source = 'sub-phase';
  } else {
    const pf = POSTURE_TO_FORM[posture];
    if (!pf) throw new Error(`phaseCasualtyTable.resolvePhaseProfile: unknown posture '${posture}' (valid: ${[...Object.keys(POSTURE_TO_FORM), ...MARCH_POSTURES].join(', ')})`);
    formKey = pf.form;
    conditionKey = pf.condition || null;
    groupKey = pf.group || null;
  }

  // role -> group (sub-phase role, then phase role); a RESERVE role makes it a reserve
  const roleSources = [subPhase && subPhase.role, phase.brigade_role].filter(Boolean);
  let roleUsed = null;
  for (const role of roleSources) {
    if (!(role in ROLE_TO_GROUP)) throw new Error(`phaseCasualtyTable.resolvePhaseProfile: unknown role '${role}' (valid: ${Object.keys(ROLE_TO_GROUP).join(', ')})`);
    roleUsed = role;
    break;
  }
  if (roleUsed === 'RESERVE') {
    formKey = 'RESERVE';
    conditionKey = null;
  } else if (roleUsed) {
    groupKey = ROLE_TO_GROUP[roleUsed];
  }
  if (phase.maneuver_group) groupKey = phase.maneuver_group;

  if (phase.special_condition) conditionKey = phase.special_condition;
  if (!conditionKey && scenarioOperationType) {
    conditionKey = OPERATION_TYPE_TO_CONDITION[scenarioOperationType];
    if (conditionKey === undefined) throw new Error(`phaseCasualtyTable.resolvePhaseProfile: operation type '${scenarioOperationType}' has no entry in OPERATION_TYPE_TO_CONDITION`);
  }
  conditionKey = conditionKey || 'NONE';

  const form = PHASE_FORMS[formKey];
  const condition = SPECIAL_CONDITIONS[conditionKey];
  if (!condition) throw new Error(`phaseCasualtyTable.resolvePhaseProfile: unknown special condition '${conditionKey}' (valid: ${Object.keys(SPECIAL_CONDITIONS).join(', ')})`);
  let groupFactor = 1.0;
  if (groupKey) {
    if (!MANEUVER_GROUPS[groupKey]) throw new Error(`phaseCasualtyTable.resolvePhaseProfile: unknown maneuver group '${groupKey}' (valid: ${Object.keys(MANEUVER_GROUPS).join(', ')})`);
    if (form.family === 'ATTACK') groupFactor = MANEUVER_GROUPS[groupKey].factor;   // groups only apply to attack forms
  }

  const formFactor = resistanceFactor(formKey, resistanceIndex);
  const conditionFactor = condition.factorForDay(dayInPhase, phase);
  return {
    form: formKey,
    group: groupKey,
    condition: conditionKey,
    resistance_index: resistanceIndex,
    resistance_level: levelOfIndex(resistanceIndex),
    form_factor: formFactor,
    group_factor: groupFactor,
    condition_factor: conditionFactor,
    total_factor: formFactor * groupFactor * conditionFactor,
    role_replaces_static: roleUsed !== null,   // a phase or sub-phase role replaces the brigade's static Tab 1 role
    day_in_phase: dayInPhase,
    source,
    status: form.status
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Enemy combat power index (Tab 2 enemy assessment -> resistance). Our own quality is NOT in it: the friendly training,
// equipment and morale already act on the casualty rate through the force quality multiplier (each factor counts once).
//   index = enemy strength / friendly strength x training x equipment x morale
// training: TRAINING_LEVELS.effectiveness_value (Dupuy CEV); morale: MORALE_LEVELS.effectiveness_multiplier;
// equipment: 1 / EQUIPMENT_LEVELS.casualty_multiplier (the casualty multiplier of the level read as an effectiveness: DERIVED).
// ---------------------------------------------------------------------------------------------------------------
export const ENEMY_POWER_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Product of the strength ratio and the effectiveness values of the constants; a log curve maps it to light / moderate / heavy resistance (anchors 0.5 / 1.0 / 2.0).'
});

export function enemyPowerIndex(assessment) {
  const { strength_ratio: ratio = 1.0, training_level: training, equipment_level: equipment, morale_level: morale } = assessment || {};
  if (typeof ratio !== 'number' || !Number.isFinite(ratio) || ratio <= 0) {
    throw new Error(`phaseCasualtyTable.enemyPowerIndex: the enemy to friendly strength ratio must be a positive number (got ${ratio})`);
  }
  const pick = (table, key, field, name) => {
    if (key === undefined || key === null) return 1.0;   // that part of the assessment is not set: neutral (the Tab 2 default is the neutral level)
    const level = table[key];
    if (!level) throw new Error(`phaseCasualtyTable.enemyPowerIndex: unknown enemy ${name} '${key}' (valid: ${Object.keys(table).join(', ')})`);
    return field(level);
  };
  const t = pick(TRAINING_LEVELS, training, (l) => l.effectiveness_value, 'training level');
  const e = pick(EQUIPMENT_LEVELS, equipment, (l) => 1 / l.casualty_multiplier, 'equipment level');
  const m = pick(MORALE_LEVELS, morale, (l) => l.effectiveness_multiplier, 'morale level');
  return { index: ratio * t * e * m, parts: { strength_ratio: ratio, training: t, equipment: e, morale: m } };
}
